import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Loader2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { listingsAPI, categoriesAPI } from '@/lib/api';
import ImageUpload from '@/components/upload/ImageUpload';

const EditListingSheet = ({ listing, open, onOpenChange, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category_id: '',
        subcategory_id: '',
        condition: 'good',
        starting_price: '',
        current_price: '',
        buy_now_price: '',
        minimum_bid_increment: '',
        shipping_cost: '',
        return_policy: 'no_returns',
        status: 'active',
        location: '',
        images: [],
        // Auction specific fields
        end_date: '',
        auction_type: 'fixed',
    });

    // Load categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await categoriesAPI.getAll();
                setCategories(res.data || []);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);

    // Initialize form data when listing changes
    useEffect(() => {
        if (listing && open) {
            // Format end_date for datetime-local input
            let formattedEndDate = '';
            if (listing.end_date) {
                try {
                    const date = new Date(listing.end_date);
                    formattedEndDate = date.toISOString().slice(0, 16);
                } catch (e) {
                    console.error('Error formatting end_date:', e);
                }
            }
            
            setFormData({
                title: listing.title || '',
                description: listing.description || '',
                category_id: listing.category_id || '',
                subcategory_id: listing.subcategory_id || '',
                condition: listing.condition || 'good',
                starting_price: listing.starting_price?.toString() || '',
                current_price: listing.current_price?.toString() || '',
                buy_now_price: listing.buy_now_price?.toString() || '',
                minimum_bid_increment: listing.minimum_bid_increment?.toString() || '1',
                shipping_cost: listing.shipping_cost?.toString() || '0',
                return_policy: listing.return_policy || 'no_returns',
                status: listing.status || 'active',
                location: listing.location || '',
                images: listing.images || [],
                end_date: formattedEndDate,
                auction_type: listing.auction_type || 'fixed',
            });
            
            // Find and set the selected category
            const cat = categories.find(c => c.category_id === listing.category_id);
            setSelectedCategory(cat || null);
        }
    }, [listing, open, categories]);

    // Update selected category when category_id changes
    useEffect(() => {
        if (formData.category_id) {
            const cat = categories.find(c => c.category_id === formData.category_id);
            setSelectedCategory(cat || null);
        }
    }, [formData.category_id, categories]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Reset subcategory when category changes
        if (field === 'category_id') {
            setFormData(prev => ({ ...prev, subcategory_id: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            toast.error('Titlul este obligatoriu');
            return;
        }
        
        setLoading(true);
        try {
            const payload = {
                title: formData.title,
                description: formData.description,
                category_id: formData.category_id || null,
                subcategory_id: formData.subcategory_id || null,
                condition: formData.condition,
                starting_price: formData.starting_price ? parseFloat(formData.starting_price) : null,
                current_price: formData.current_price ? parseFloat(formData.current_price) : null,
                buy_now_price: formData.buy_now_price ? parseFloat(formData.buy_now_price) : null,
                minimum_bid_increment: formData.minimum_bid_increment ? parseFloat(formData.minimum_bid_increment) : null,
                shipping_cost: formData.shipping_cost ? parseFloat(formData.shipping_cost) : 0,
                return_policy: formData.return_policy,
                status: formData.status,
                location: formData.location,
                images: formData.images,
            };
            
            // Add auction-specific fields if this is an auction
            if (listing?.listing_type === 'auction') {
                if (formData.end_date) {
                    payload.end_date = new Date(formData.end_date).toISOString();
                }
                payload.auction_type = formData.auction_type;
            }

            await listingsAPI.update(listing.listing_id, payload);
            toast.success('Anunțul a fost actualizat cu succes');
            onOpenChange(false);
            if (onSuccess) onSuccess();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Eroare la actualizarea anunțului');
        } finally {
            setLoading(false);
        }
    };

    const isAuction = listing?.listing_type === 'auction';

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
                <SheetHeader>
                    <SheetTitle>Editare Anunț</SheetTitle>
                    <SheetDescription>
                        Modifică detaliile anunțului. Toate modificările vor fi salvate imediat.
                    </SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} className="space-y-6 mt-6">
                    {/* Title */}
                    <div className="space-y-2">
                        <Label htmlFor="edit-title">Titlu *</Label>
                        <Input
                            id="edit-title"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder="Titlul anunțului"
                            required
                        />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="edit-description">Descriere</Label>
                        <Textarea
                            id="edit-description"
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Descriere detaliată..."
                            rows={4}
                        />
                    </div>

                    {/* Category & Subcategory */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Categorie</Label>
                            <Select
                                value={formData.category_id}
                                onValueChange={(value) => handleChange('category_id', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selectează categoria" />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((cat) => (
                                        <SelectItem key={cat.category_id} value={cat.category_id}>
                                            {cat.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Subcategorie</Label>
                            <Select
                                value={formData.subcategory_id || 'none'}
                                onValueChange={(value) => handleChange('subcategory_id', value === 'none' ? '' : value)}
                                disabled={!selectedCategory?.subcategories?.length}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selectează subcategoria" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Fără subcategorie</SelectItem>
                                    {selectedCategory?.subcategories?.map((sub) => (
                                        <SelectItem key={sub.subcategory_id} value={sub.subcategory_id}>
                                            {sub.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Condition & Status */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Stare</Label>
                            <Select
                                value={formData.condition}
                                onValueChange={(value) => handleChange('condition', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="mint">Mint (Nou sigilat)</SelectItem>
                                    <SelectItem value="excellent">Excelentă</SelectItem>
                                    <SelectItem value="very_good">Foarte bună</SelectItem>
                                    <SelectItem value="good">Bună</SelectItem>
                                    <SelectItem value="fair">Acceptabilă</SelectItem>
                                    <SelectItem value="poor">Slabă</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Status anunț</Label>
                            <Select
                                value={formData.status}
                                onValueChange={(value) => handleChange('status', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">Activ</SelectItem>
                                    <SelectItem value="sold">Vândut</SelectItem>
                                    <SelectItem value="cancelled">Anulat</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Prices */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>{isAuction ? 'Preț de pornire' : 'Preț'}</Label>
                            <div className="relative">
                                <Input
                                    type="number"
                                    value={formData.starting_price}
                                    onChange={(e) => handleChange('starting_price', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                    className="pr-12"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">RON</span>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Preț curent</Label>
                            <div className="relative">
                                <Input
                                    type="number"
                                    value={formData.current_price}
                                    onChange={(e) => handleChange('current_price', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                    className="pr-12"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">RON</span>
                            </div>
                        </div>
                    </div>

                    {/* Auction specific fields */}
                    {isAuction && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Preț cumpără acum</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        value={formData.buy_now_price}
                                        onChange={(e) => handleChange('buy_now_price', e.target.value)}
                                        placeholder="0"
                                        min="0"
                                        step="0.01"
                                        className="pr-12"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">RON</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Increment minim</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        value={formData.minimum_bid_increment}
                                        onChange={(e) => handleChange('minimum_bid_increment', e.target.value)}
                                        placeholder="1"
                                        min="1"
                                        step="1"
                                        className="pr-12"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">RON</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Auction end date and type (Admin only) */}
                    {isAuction && (
                        <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="col-span-2">
                                <p className="text-xs text-blue-600 font-medium mb-2">⚙️ Setări licitație (doar admin)</p>
                            </div>
                            <div className="space-y-2">
                                <Label>Sfârșit licitație</Label>
                                <Input
                                    type="datetime-local"
                                    value={formData.end_date}
                                    onChange={(e) => handleChange('end_date', e.target.value)}
                                    className="bg-white"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Tip licitație</Label>
                                <Select
                                    value={formData.auction_type}
                                    onValueChange={(value) => handleChange('auction_type', value)}
                                >
                                    <SelectTrigger className="bg-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="fixed">Timp fix</SelectItem>
                                        <SelectItem value="variable">Timp variabil</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-[10px] text-muted-foreground">
                                    {formData.auction_type === 'fixed' 
                                        ? 'Licitația se termină la ora stabilită'
                                        : 'Se prelungește cu 5 min la fiecare ofertă în ultimele minute'
                                    }
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Shipping & Return */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Cost transport</Label>
                            <div className="relative">
                                <Input
                                    type="number"
                                    value={formData.shipping_cost}
                                    onChange={(e) => handleChange('shipping_cost', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                    className="pr-12"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">RON</span>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Politica retur</Label>
                            <Select
                                value={formData.return_policy}
                                onValueChange={(value) => handleChange('return_policy', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="no_returns">Nu acceptă retururi</SelectItem>
                                    <SelectItem value="7_days">Retur în 7 zile</SelectItem>
                                    <SelectItem value="14_days">Retur în 14 zile</SelectItem>
                                    <SelectItem value="30_days">Retur în 30 zile</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-2">
                        <Label>Locație</Label>
                        <Input
                            value={formData.location}
                            onChange={(e) => handleChange('location', e.target.value)}
                            placeholder="Ex: București, Cluj-Napoca..."
                        />
                    </div>

                    {/* Images */}
                    <div className="space-y-2">
                        <Label>Imagini</Label>
                        <ImageUpload 
                            images={formData.images} 
                            setImages={(newImages) => {
                                if (typeof newImages === 'function') {
                                    setFormData(prev => ({ ...prev, images: newImages(prev.images) }));
                                } else {
                                    setFormData(prev => ({ ...prev, images: newImages }));
                                }
                            }}
                            maxImages={15}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                        <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            onClick={() => onOpenChange(false)}
                            disabled={loading}
                        >
                            <X className="w-4 h-4 mr-2" />
                            Anulează
                        </Button>
                        <Button
                            type="submit"
                            className="flex-1"
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Salvează modificările
                        </Button>
                    </div>
                </form>
            </SheetContent>
        </Sheet>
    );
};

export default EditListingSheet;
